import type {
  BusinessLine,
  CampaignDesiredStateDto,
  Country,
  Placement,
  ProgramDto,
  UpdateCampaignDto,
  WorkflowDto,
} from './generated/leela-api-generated';

/**
 * @rtk-query/codegen-openapi converts some enum values to inline string literal
 * unions. This is problematic when we want to iterate over these values as in
 * the case for drop down menus.
 *
 * While TS allows conversion of arrays to literal unions, you cannot easily go
 * the opposite direction.
 *
 * This compromise allows us to iterate over the values while maintaining some
 * level of type safety.
 *
 * It is a stop gap until we can find a better solution.
 */
export const CampaignPlacementTypes: Record<string, Placement['placementType']> = {
  FACEBOOK_FEED: 'FACEBOOK_FEED',
  FACEBOOK_VIDEO_FEEDS: 'FACEBOOK_VIDEO_FEEDS',
  FACEBOOK_STORY: 'FACEBOOK_STORY',
  FACEBOOK_SEARCH: 'FACEBOOK_SEARCH',
  FACEBOOK_INSTREAM_VIDEO: 'FACEBOOK_INSTREAM_VIDEO',
  FACEBOOK_REELS: 'FACEBOOK_REELS',
  FACEBOOK_PROFILE_FEED: 'FACEBOOK_PROFILE_FEED',
  INSTAGRAM_STREAM: 'INSTAGRAM_STREAM',
  INSTAGRAM_STORY: 'INSTAGRAM_STORY',
  INSTAGRAM_EXPLORE: 'INSTAGRAM_EXPLORE',
  INSTAGRAM_EXPLORE_HOME: 'INSTAGRAM_EXPLORE_HOME',
  INSTAGRAM_REELS: 'INSTAGRAM_REELS',
  INSTAGRAM_PROFILE_FEED: 'INSTAGRAM_PROFILE_FEED',
  INSTAGRAM_SEARCH: 'INSTAGRAM_SEARCH',
};

const CampaignPublisherOptions: Record<string, CampaignDesiredStateDto['publisher']> = {
  META: 'meta',
  TIKTOK: 'tiktok',
  ADSP: 'adsp',
  GOOGLE: 'google',
};

export const CampaignPublishers: Record<string, CampaignDesiredStateDto['publisher']> = CampaignPublisherOptions;

export const CampaignStatuses: Record<string, CampaignDesiredStateDto['status']> = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
  DELETED: 'DELETED',
  PAUSED: 'PAUSED',
  SUSPENDED: 'SUSPENDED',
};

export const BusinessLines: Record<string, BusinessLine> = {
  SVOD: 'SVOD',
  TVOD: 'TVOD',
  CHANNEL: 'CHANNEL',
};

export const SportsBusinessLines: Record<string, BusinessLine> = {
  SPORTS_SVOD: 'Sports SVOD',
  SPORTS_TVOD: 'Sports TVOD',
  SPORTS_CHANNEL: 'Sports Channel',
};

export const PERMISSION_LIST_PAGE_SIZE = 100;

/**
 * Permission scope deliberately covers every publisher and business line. Unlike
 * getAvailablePublishers() and FormCampaignEdit, which apply per-stage feature flags,
 * durable group configuration must not depend on which flags are currently enabled.
 */
export const PermissionScopePublishers = Object.values(CampaignPublishers);
export const PermissionScopeBusinessLines = [
  ...Object.values(BusinessLines),
  ...Object.values(SportsBusinessLines),
];

/** True when the given business line is one of the sports business lines. */
export const isSportsBusinessLine = (businessLine?: string): boolean =>
  Object.values(SportsBusinessLines).includes(businessLine as BusinessLine);

export const Countries: Country[] = [
  'AE', 'AR', 'AU', 'BE', 'BO', 'BR', 'CA', 'CH', 'CL', 'CO', 'CR', 'CZ', 'DE', 'DK', 'DO',
  'EC', 'EG', 'ES', 'FI', 'FR', 'GB', 'GT', 'HN', 'HU', 'ID', 'IE', 'IN', 'IT', 'JP', 'MX',
  'MY', 'NG', 'NI', 'NL', 'NO', 'NZ', 'PA', 'PE', 'PH', 'PL', 'PR', 'PT', 'PY', 'RO', 'SA',
  'SE', 'SG', 'SV', 'TH', 'TR', 'US', 'UY', 'VE', 'ZA',
];

export const CampaignDestinationUrlConfigTypes: Record<string, CampaignDesiredStateDto['destinationUrlConfig']['type']> = {
  DETAIL: 'DETAIL',
  HOME: 'HOME',
  LANDING: 'LANDING',
};

export const CampaignUpdatePriorities: Record<string, UpdateCampaignDto['priority']> = {
  IMMEDIATE: 'IMMEDIATE',
  SCHEDULED: 'SCHEDULED',
};

export const ProgramStatuses: Record<string, ProgramDto['status']> = {
  ACTIVE: 'ACTIVE',
  DELETED: 'DELETED',
  PAUSED: 'PAUSED',
};

export const WorkflowStatuses: Record<string, WorkflowDto['status']> = {
  QUEUED: 'QUEUED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  IN_PROGRESS: 'IN_PROGRESS',
  ROLLBACK_COMPLETE: 'ROLLBACK_COMPLETE',
  ROLLBACK_FAILED: 'ROLLBACK_FAILED',
  ROLLBACK_IN_PROGRESS: 'ROLLBACK_IN_PROGRESS',
  DELETED: 'DELETED',
};
